import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useAuth } from 'util/auth'

function AuthForm(props) {
  const auth = useAuth()

  const [pending, setPending] = useState(false)
  const {
    handleSubmit,
    register,
    formState: { errors },
    getValues,
  } = useForm()

  const submitHandlersByType = {
    signin: ({ email, pass }) => {
      return auth.signin(email, pass).then((user) => {
        // Call auth complete handler
        props.onAuth(user)
      })
    },
    signup: ({ email, pass }) => {
      return auth
        .signup({ email, password: pass, sendToTrial: true })
        .then((user) => {
          // Call auth complete handler
          props.onAuth(user)
        })
    },
    forgotpass: ({ email }) => {
      return auth.sendPasswordResetEmail(email).then(() => {
        setPending(false)
        // Show success alert message
        props.onFormAlert({
          type: 'success',
          message: 'Password reset email sent',
        })
      })
    },
    changepass: ({ pass }) => {
      return auth.confirmPasswordReset(pass).then(() => {
        setPending(false)
        // Show success alert message
        props.onFormAlert({
          type: 'success',
          message: 'Your password has been changed',
        })
      })
    },
  }

  // Handle form submission
  const onSubmit = async ({ email, pass }) => {
    // Show pending indicator
    setPending(true)

    // Validate email before signup if it's a signup form
    if (props.type === 'signup') {
      console.log('email', email)
      try {
        const validationResponse = await fetch(
          `/api/validate-email?email=${encodeURIComponent(email)}`
        )

        if (!validationResponse.ok) {
          throw new Error('Email validation failed. Please try again.')
        }

        const validationData = await validationResponse.json()

        if (validationData.isDisposable) {
          setPending(false)
          props.onFormAlert({
            type: 'error',
            message: 'Something went wrong. Please try again later.',
          })
          return
        }
      } catch (error) {
        setPending(false)
        props.onFormAlert({
          type: 'error',
          message:
            error instanceof Error
              ? error.message
              : 'Email validation failed. Please try again.',
        })
        return
      }
    }

    // Call submit handler for auth type
    submitHandlersByType[props.type]({
      email,
      pass,
    }).catch((error) => {
      setPending(false)
      // Show error alert message
      props.onFormAlert({
        type: 'error',
        message: error.message,
      })
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {['signup', 'signin', 'forgotpass'].includes(props.type) && (
        <div className="mb-2">
          <input
            className="w-full rounded border border-gray-300 bg-white py-1 px-3 leading-8 outline-none focus:border-blue-500 focus:ring-1"
            {...register('email', {
              required: 'Please enter an email',
            })}
            type="email"
            placeholder="Email"
          />

          {errors.email && (
            <p className="mt-1 text-left text-sm text-red-600">
              {errors.email.message?.toString()}
            </p>
          )}
        </div>
      )}

      {['signup', 'signin', 'changepass'].includes(props.type) && (
        <div className="mb-2">
          <input
            className="w-full rounded border border-gray-300 bg-white py-1 px-3 leading-8 outline-none focus:border-blue-500 focus:ring-1"
            {...register('pass', {
              required: 'Please enter a password',
            })}
            type="password"
            placeholder="Password"
          />

          {errors.pass && (
            <p className="mt-1 text-left text-sm text-red-600">
              {errors.pass.message?.toString()}
            </p>
          )}
        </div>
      )}

      {['signup', 'changepass'].includes(props.type) && (
        <div className="mb-2">
          <input
            className="w-full rounded border border-gray-300 bg-white py-1 px-3 leading-8 outline-none focus:border-blue-500 focus:ring-1"
            {...register('confirmPass', {
              required: 'Please enter your password again',
              validate: (value) => {
                if (value === getValues().pass) {
                  return true
                } else {
                  return "This doesn't match your password"
                }
              },
            })}
            type="password"
            placeholder="Confirm Password"
          />

          {errors.confirmPass && (
            <p className="mt-1 text-left text-sm text-red-600">
              {errors.confirmPass.message?.toString()}
            </p>
          )}
        </div>
      )}

      <button
        className="w-full rounded border-0 bg-blue-500 py-2 px-4 text-white hover:bg-blue-600 focus:outline-none"
        disabled={pending}
      >
        {pending ? '...' : props.buttonAction}
      </button>
    </form>
  )
}

export default AuthForm
